import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import { API } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

// Amenity icon mapping
const AMENITY_ICONS = {
  'Swimming Pool': { icon: 'fa-swimming-pool', color: 'text-blue-500', bg: 'bg-blue-50' },
  'pool': { icon: 'fa-swimming-pool', color: 'text-blue-500', bg: 'bg-blue-50' },
  'Garden': { icon: 'fa-leaf', color: 'text-green-500', bg: 'bg-green-50' },
  'garden': { icon: 'fa-leaf', color: 'text-green-500', bg: 'bg-green-50' },
  'Security': { icon: 'fa-shield-alt', color: 'text-gray-600', bg: 'bg-gray-50' },
  'security': { icon: 'fa-shield-alt', color: 'text-gray-600', bg: 'bg-gray-50' },
  'Garage': { icon: 'fa-car', color: 'text-slate-500', bg: 'bg-slate-50' },
  'garage': { icon: 'fa-car', color: 'text-slate-500', bg: 'bg-slate-50' },
  'Air Conditioning': { icon: 'fa-snowflake', color: 'text-cyan-500', bg: 'bg-cyan-50' },
  'airConditioning': { icon: 'fa-snowflake', color: 'text-cyan-500', bg: 'bg-cyan-50' },
  'Pet Friendly': { icon: 'fa-paw', color: 'text-yellow-600', bg: 'bg-yellow-50' },
  'petFriendly': { icon: 'fa-paw', color: 'text-yellow-600', bg: 'bg-yellow-50' },
  'Fibre Internet': { icon: 'fa-wifi', color: 'text-indigo-500', bg: 'bg-indigo-50' },
  'fibre': { icon: 'fa-wifi', color: 'text-indigo-500', bg: 'bg-indigo-50' },
  'WiFi': { icon: 'fa-wifi', color: 'text-indigo-500', bg: 'bg-indigo-50' },
  'Borehole': { icon: 'fa-tint', color: 'text-blue-600', bg: 'bg-blue-50' },
  'borehole': { icon: 'fa-tint', color: 'text-blue-600', bg: 'bg-blue-50' },
  'Solar Panels': { icon: 'fa-solar-panel', color: 'text-yellow-500', bg: 'bg-yellow-50' },
  'solarPanels': { icon: 'fa-solar-panel', color: 'text-yellow-500', bg: 'bg-yellow-50' },
  'Staff Quarters': { icon: 'fa-home', color: 'text-purple-500', bg: 'bg-purple-50' },
  'staffAccommodation': { icon: 'fa-home', color: 'text-purple-500', bg: 'bg-purple-50' },
  'Parking': { icon: 'fa-parking', color: 'text-blue-500', bg: 'bg-blue-50' },
  'Balcony': { icon: 'fa-building', color: 'text-teal-500', bg: 'bg-teal-50' },
  'Gym': { icon: 'fa-dumbbell', color: 'text-red-500', bg: 'bg-red-50' },
  'Laundry': { icon: 'fa-tshirt', color: 'text-pink-500', bg: 'bg-pink-50' },
  'Fireplace': { icon: 'fa-fire', color: 'text-orange-500', bg: 'bg-orange-50' },
  'default': { icon: 'fa-check-circle', color: 'text-emerald-500', bg: 'bg-emerald-50' }
};

// Get amenity icon config
const getAmenityIcon = (amenity) => {
  return AMENITY_ICONS[amenity] || AMENITY_ICONS['default'];
};

// Amenities Carousel Component
const AmenitiesCarousel = ({ amenities }) => {
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const VISIBLE_COUNT = 6;

  const displayedAmenities = showAll ? amenities : amenities.slice(0, VISIBLE_COUNT);
  const hasMore = amenities.length > VISIBLE_COUNT;

  const checkScrollPosition = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 10);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 200;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(checkScrollPosition, 300);
    }
  };

  useEffect(() => {
    checkScrollPosition();
    const ref = scrollRef.current;
    if (ref) {
      ref.addEventListener('scroll', checkScrollPosition);
      return () => ref.removeEventListener('scroll', checkScrollPosition);
    }
  }, [showAll]);

  if (!amenities || amenities.length === 0) return null;

  return (
    <section aria-labelledby="amenities-heading" className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 id="amenities-heading" className="text-xl font-semibold text-gray-800">
          Amenities & Features
        </h2>
        {hasMore && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1 transition-colors"
            data-testid="show-more-amenities"
          >
            {showAll ? (
              <>Show less <i className="fas fa-chevron-up text-xs"></i></>
            ) : (
              <>Show all ({amenities.length}) <i className="fas fa-chevron-down text-xs"></i></>
            )}
          </button>
        )}
      </div>

      <div className="relative">
        {/* Left Arrow */}
        {showLeftArrow && !showAll && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white shadow-lg rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-all opacity-90 hover:opacity-100"
            aria-label="Scroll left"
          >
            <i className="fas fa-chevron-left text-sm"></i>
          </button>
        )}

        {/* Amenities Container */}
        <div
          ref={scrollRef}
          className={`${showAll 
            ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3' 
            : 'flex gap-3 overflow-x-auto scrollbar-hide scroll-smooth snap-x snap-mandatory pb-2'
          }`}
          style={!showAll ? { scrollbarWidth: 'none', msOverflowStyle: 'none' } : {}}
        >
          {displayedAmenities.map((amenity, i) => {
            const iconConfig = getAmenityIcon(amenity);
            return (
              <div
                key={i}
                className={`${showAll ? '' : 'flex-shrink-0 snap-start'} ${iconConfig.bg} rounded-xl p-4 min-w-[140px] transition-transform hover:scale-105 cursor-default`}
                style={{ animationDelay: `${i * 50}ms` }}
              >
                <div className={`w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center mb-2`}>
                  <i className={`fas ${iconConfig.icon} ${iconConfig.color} text-lg`}></i>
                </div>
                <p className="text-sm font-medium text-gray-700 line-clamp-2">{amenity}</p>
              </div>
            );
          })}
        </div>

        {/* Right Arrow */}
        {showRightArrow && !showAll && amenities.length > 3 && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white shadow-lg rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-all opacity-90 hover:opacity-100"
            aria-label="Scroll right"
          >
            <i className="fas fa-chevron-right text-sm"></i>
          </button>
        )}
      </div>
    </section>
  );
};

// Lazy loaded image component

// Lazy loaded image component
const LazyImage = ({ src, alt, className }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  return (
    <div className={`relative ${className}`}>
      {!loaded && !error && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
          <i className="fas fa-image text-gray-400 text-3xl"></i>
        </div>
      )}
      {error ? (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
          <i className="fas fa-home text-gray-400 text-4xl"></i>
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
          className={`w-full h-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        />
      )}
    </div>
  );
};

const ListingDetailPage = () => {
  const { listingId } = useParams();
  const navigate = useNavigate();
  
  const [listing, setListing] = useState(null);
  const [aeoData, setAeoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showFAQ, setShowFAQ] = useState(true);

  // Format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      maximumFractionDigits: 0
    }).format(price);
  };

  // Fetch listing data
  const fetchListing = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch listing details and AEO schema in parallel
      const [listingRes, aeoRes] = await Promise.all([
        axios.get(`${API}/listings/${listingId}`),
        axios.get(`${API}/seo/aeo-schema/${listingId}`)
      ]);
      
      setListing(listingRes.data);
      setAeoData(aeoRes.data);
    } catch (err) {
      console.error('Error fetching listing:', err);
      if (err.response?.status === 404) {
        setError('Property not found.');
      } else {
        setError('Failed to load property details.');
      }
    } finally {
      setLoading(false);
    }
  }, [listingId]);

  useEffect(() => {
    fetchListing();
  }, [fetchListing]);

  // Handle WhatsApp contact
  const handleWhatsApp = () => {
    if (!listing?.whatsappNumber && !listing?.contactNumber) {
      toast.error('No contact number available');
      return;
    }
    const phone = (listing.whatsappNumber || listing.contactNumber).replace(/\D/g, '');
    const message = `Hi, I'm interested in this property on FlippyHomes: ${listing.title} - ${formatPrice(listing.price)}`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  // Generate combined Schema.org JSON-LD
  const generateSchemaData = () => {
    if (!aeoData) return null;
    
    return JSON.stringify([
      aeoData.realEstateSchema,
      aeoData.faqSchema
    ]);
  };

  // Loading state
  if (loading) {
    return (
      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-64 mb-6" />
          <div className="aspect-video bg-gray-200 rounded-lg mb-6" />
          <div className="h-8 bg-gray-200 rounded w-3/4 mb-4" />
          <div className="h-10 bg-gray-200 rounded w-48 mb-6" />
          <div className="grid grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded" />
            ))}
          </div>
        </div>
      </main>
    );
  }

  // Error state
  if (error) {
    return (
      <main className="max-w-6xl mx-auto px-4 py-12 text-center">
        <div className="w-20 h-20 mx-auto mb-6 bg-red-50 rounded-full flex items-center justify-center">
          <i className="fas fa-exclamation-triangle text-3xl text-red-400"></i>
        </div>
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Property Not Found</h1>
        <p className="text-gray-600 mb-6">{error}</p>
        <Button onClick={() => navigate('/marketplace')}>
          Browse All Properties
        </Button>
      </main>
    );
  }

  if (!listing || !aeoData) return null;

  const specs = listing.specifications || {};
  const amenities = listing.amenities || specs.amenities || {};
  const images = listing.images || [];
  const quickAnswers = aeoData.quickAnswers;
  const faqItems = aeoData.faqItems || [];

  // Get all amenity items as an array.
  // Handles BOTH the new key set (pool, garden, security, garage, airConditioning,
  // petFriendly, fibre, borehole, solarPanels, staffAccommodation) AND the legacy
  // keys persisted by older listings (hasPool, hasGarden, hasGarage, hasFiber, isPetFriendly),
  // so every existing listing renders the amenities the agent originally selected.
  const getAmenityList = () => {
    if (Array.isArray(amenities)) {
      return [...amenities, ...(listing.customAmenities || [])];
    }

    const items = [];
    const truthy = (...keys) => keys.some(k => amenities?.[k]);

    if (truthy('pool', 'hasPool')) items.push('Swimming Pool');
    if (truthy('garden', 'hasGarden')) items.push('Garden');
    if (truthy('security')) items.push('Security');
    if (truthy('garage', 'hasGarage')) items.push('Garage');
    if (truthy('airConditioning')) items.push('Air Conditioning');
    if (truthy('petFriendly', 'isPetFriendly')) items.push('Pet Friendly');
    if (truthy('fibre', 'hasFiber')) items.push('Fibre Internet');
    if (truthy('borehole')) items.push('Borehole');
    if (truthy('solarPanels')) items.push('Solar Panels');
    if (truthy('staffAccommodation')) items.push('Staff Quarters');

    // Append custom amenities added by the agent
    if (Array.isArray(listing.customAmenities)) {
      items.push(...listing.customAmenities);
    }
    return items;
  };
  const amenityList = getAmenityList();

  return (
    <>
      {/* SEO Head Tags with AEO Optimization */}
      <Helmet>
        <title>{listing.title} | {quickAnswers.price} | Flippy Homes</title>
        <meta name="description" content={`${listing.title} - ${quickAnswers.price}. ${quickAnswers.bedrooms} bedrooms, ${quickAnswers.bathrooms} bathrooms. Located in ${quickAnswers.location}. View photos and contact the agent.`} />
        <link rel="canonical" href={`${window.location.origin}/listing/${listingId}`} />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={`${listing.title} | ${quickAnswers.price}`} />
        <meta property="og:description" content={`${quickAnswers.propertyType} ${quickAnswers.listingType.toLowerCase()} - ${quickAnswers.bedrooms} beds, ${quickAnswers.bathrooms} baths in ${quickAnswers.location}`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${window.location.origin}/listing/${listingId}`} />
        {images[0] && <meta property="og:image" content={typeof images[0] === 'object' ? images[0].full || images[0].medium : images[0]} />}
        <meta property="og:site_name" content="Flippy Homes" />
        <meta property="og:locale" content="en_ZA" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${listing.title} | ${quickAnswers.price}`} />
        <meta name="twitter:description" content={`${quickAnswers.propertyType} ${quickAnswers.listingType.toLowerCase()} in ${quickAnswers.location}`} />
        
        {/* Schema.org Structured Data (RealEstateListing + FAQPage) */}
        <script type="application/ld+json">
          {generateSchemaData()}
        </script>
      </Helmet>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-4" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-emerald-600">Home</Link>
          <i className="fas fa-chevron-right text-xs text-gray-400"></i>
          <Link to="/marketplace" className="hover:text-emerald-600">Properties</Link>
          <i className="fas fa-chevron-right text-xs text-gray-400"></i>
          <span className="text-gray-700 font-medium line-clamp-1">{listing.title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Left Side */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Gallery */}
            <div className="space-y-3">
              <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                <LazyImage
                  src={typeof images[activeImageIndex] === 'object' 
                    ? images[activeImageIndex]?.full || images[activeImageIndex]?.medium 
                    : images[activeImageIndex]}
                  alt={`${listing.title} - Image ${activeImageIndex + 1}`}
                  className="w-full h-full"
                />
              </div>
              
              {images.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {images.slice(0, 6).map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImageIndex(i)}
                      className={`flex-shrink-0 w-20 h-16 rounded-md overflow-hidden border-2 transition-colors ${
                        i === activeImageIndex ? 'border-emerald-500' : 'border-transparent'
                      }`}
                    >
                      <LazyImage
                        src={typeof img === 'object' ? img?.thumbnail || img?.medium : img}
                        alt={`Thumbnail ${i + 1}`}
                        className="w-full h-full"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Property Title & Price - H1 (Voice Search Optimized) */}
            <div>
              <Badge className={`mb-2 ${listing.type === 'sale' ? 'bg-emerald-600' : 'bg-blue-600'}`}>
                {listing.type === 'sale' ? 'For Sale' : 'To Rent'}
              </Badge>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                {listing.title}
              </h1>
              <p className="text-2xl sm:text-3xl font-bold text-emerald-600">
                {quickAnswers.price}
              </p>
              <p className="text-gray-500 mt-1">
                <i className="fas fa-map-marker-alt mr-2"></i>
                {quickAnswers.location}
              </p>
            </div>

            {/* Quick Answers Section (AEO Optimized) */}
            <section className="bg-emerald-50 rounded-lg p-4 sm:p-6" aria-labelledby="quick-answers-heading">
              <h2 id="quick-answers-heading" className="text-lg font-semibold text-gray-800 mb-4">
                Property Quick Facts
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                  <i className="fas fa-tag text-emerald-600 text-xl mb-2"></i>
                  <p className="text-xs text-gray-500">Price</p>
                  <p className="font-bold text-gray-800">{quickAnswers.price}</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                  <i className="fas fa-bed text-emerald-600 text-xl mb-2"></i>
                  <p className="text-xs text-gray-500">Bedrooms</p>
                  <p className="font-bold text-gray-800">{quickAnswers.bedrooms}</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                  <i className="fas fa-bath text-emerald-600 text-xl mb-2"></i>
                  <p className="text-xs text-gray-500">Bathrooms</p>
                  <p className="font-bold text-gray-800">{quickAnswers.bathrooms}</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                  <i className="fas fa-expand text-emerald-600 text-xl mb-2"></i>
                  <p className="text-xs text-gray-500">Floor Size</p>
                  <p className="font-bold text-gray-800">{quickAnswers.size}</p>
                </div>
              </div>
            </section>

            {/* Property Description - H2 */}
            <section aria-labelledby="description-heading">
              <h2 id="description-heading" className="text-xl font-semibold text-gray-800 mb-3">
                About This Property
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-600 whitespace-pre-line">
                  {listing.description || 'No description available for this property.'}
                </p>
              </div>
            </section>

            {/* Amenities & Features Carousel */}
            <AmenitiesCarousel amenities={amenityList} />

            {/* FAQ Section (AEO Critical) */}
            <section className="border rounded-lg p-4 sm:p-6" aria-labelledby="faq-heading">
              <div className="flex items-center justify-between mb-4">
                <h2 id="faq-heading" className="text-xl font-semibold text-gray-800">
                  Frequently Asked Questions
                </h2>
                <button 
                  onClick={() => setShowFAQ(!showFAQ)}
                  className="text-emerald-600 text-sm hover:underline"
                >
                  {showFAQ ? 'Hide' : 'Show'}
                </button>
              </div>
              
              {showFAQ && (
                <div className="space-y-4">
                  {faqItems.map((faq, i) => (
                    <div key={i} className="border-b border-gray-100 pb-4 last:border-0 last:pb-0">
                      <h3 className="font-medium text-gray-800 mb-2">
                        <i className="fas fa-question-circle text-emerald-500 mr-2"></i>
                        {faq.question}
                      </h3>
                      <p className="text-gray-600 pl-6">
                        {faq.answer}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Location Section - H2 */}
            <section aria-labelledby="location-heading">
              <h2 id="location-heading" className="text-xl font-semibold text-gray-800 mb-3">
                Location
              </h2>
              <div className="bg-gray-100 rounded-lg p-4">
                <p className="text-gray-600">
                  <i className="fas fa-map-marker-alt text-emerald-600 mr-2"></i>
                  <strong>Address:</strong> {quickAnswers.location}
                </p>
              </div>
            </section>
          </div>

          {/* Sidebar - Right Side */}
          <div className="lg:col-span-1">
            <div className="sticky top-4 space-y-4">
              {/* Agent Information Section */}
              <Card className="shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-emerald-500 to-teal-500 h-16"></div>
                <CardContent className="p-6 -mt-8">
                  <div className="text-center mb-4">
                    {/* Agent Profile Image (Circular) */}
                    <div className="relative inline-block">
                      <div className="w-20 h-20 rounded-full bg-white shadow-lg flex items-center justify-center overflow-hidden border-4 border-white mx-auto">
                        {listing.agentProfileImage ? (
                          <img 
                            src={listing.agentProfileImage} 
                            alt={listing.listingAgentName || 'Agent'} 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-emerald-50 flex items-center justify-center">
                            <i className="fas fa-user-tie text-3xl text-emerald-600"></i>
                          </div>
                        )}
                      </div>
                      {/* Verified Badge - Only shows if agent has profile photo */}
                      {listing.agentProfileImage && (
                        <div 
                          className="absolute -bottom-1 -right-1 w-7 h-7 bg-blue-500 rounded-full flex items-center justify-center shadow-md"
                          title="Verified Agent"
                          data-testid="verified-badge"
                        >
                          <i className="fas fa-check text-white text-xs"></i>
                        </div>
                      )}
                    </div>
                    
                    {/* Agent Name */}
                    <h3 className="font-semibold text-gray-800 text-lg mt-3" data-testid="agent-name">
                      {listing.listingAgentName || listing.companyName || 'Property Agent'}
                    </h3>
                    <p className="text-sm text-gray-500 flex items-center justify-center gap-1">
                      <i className="fas fa-user-check text-emerald-500 text-xs"></i>
                      Licensed Property Agent
                    </p>
                  </div>
                  
                  {/* Contact Buttons */}
                  <div className="space-y-3">
                    {/* WhatsApp Button - Primary CTA */}
                    <Button 
                      onClick={handleWhatsApp}
                      className="w-full bg-green-500 hover:bg-green-600 text-white font-medium"
                      data-testid="whatsapp-cta"
                    >
                      <i className="fab fa-whatsapp mr-2 text-lg"></i>
                      WhatsApp Agent
                    </Button>
                    
                    {/* Phone Button */}
                    {(listing.agentPhone || listing.contactNumber || listing.whatsappNumber) && (
                      <Button 
                        variant="outline"
                        className="w-full"
                        onClick={() => window.location.href = `tel:${listing.agentPhone || listing.contactNumber || listing.whatsappNumber}`}
                      >
                        <i className="fas fa-phone mr-2"></i>
                        Call {listing.agentPhone || listing.contactNumber || listing.whatsappNumber}
                      </Button>
                    )}
                  </div>
                  
                  {/* Listing Reference */}
                  {listing.listingNumber && (
                    <div className="mt-4 pt-4 border-t text-center">
                      <p className="text-xs text-gray-400">
                        Listing Ref: <span className="font-medium text-gray-600">{listing.listingNumber}</span>
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Property Summary Card with Rates & Levies */}
              <Card>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-gray-800 mb-3">Property Summary</h3>
                  <ul className="space-y-2 text-sm">
                    <li className="flex justify-between">
                      <span className="text-gray-500">Type</span>
                      <span className="font-medium">{quickAnswers.propertyType}</span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-gray-500">Status</span>
                      <span className="font-medium">{quickAnswers.listingType}</span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-gray-500">Bedrooms</span>
                      <span className="font-medium">{quickAnswers.bedrooms}</span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-gray-500">Bathrooms</span>
                      <span className="font-medium">{quickAnswers.bathrooms}</span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-gray-500">Floor Size</span>
                      <span className="font-medium">{quickAnswers.size}</span>
                    </li>
                    {/* Rates & Taxes */}
                    {listing.ratesAndTaxes > 0 && (
                      <li className="flex justify-between border-t pt-2 mt-2">
                        <span className="text-gray-500">Rates & Taxes</span>
                        <span className="font-medium">R{listing.ratesAndTaxes.toLocaleString()}/mo</span>
                      </li>
                    )}
                    {/* Levies */}
                    {listing.levies > 0 && (
                      <li className="flex justify-between">
                        <span className="text-gray-500">Levies</span>
                        <span className="font-medium">R{listing.levies.toLocaleString()}/mo</span>
                      </li>
                    )}
                  </ul>
                </CardContent>
              </Card>

              {/* Share Card */}
              <Card>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-gray-800 mb-3">Share Property</h3>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const url = window.location.href;
                        const text = `Check out this property: ${listing.title} - ${quickAnswers.price}`;
                        window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
                      }}
                    >
                      <i className="fab fa-whatsapp text-green-500"></i>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const url = window.location.href;
                        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
                      }}
                    >
                      <i className="fab fa-facebook text-blue-600"></i>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const url = window.location.href;
                        const text = `${listing.title} - ${quickAnswers.price}`;
                        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
                      }}
                    >
                      <i className="fab fa-twitter text-sky-500"></i>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                        toast.success('Link copied!');
                      }}
                    >
                      <i className="fas fa-link text-gray-500"></i>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Browse More Section */}
        <section className="mt-12 pt-8 border-t">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Explore More Properties
          </h2>
          <div className="flex flex-wrap gap-3">
            <Link to="/marketplace" className="text-emerald-600 hover:underline">
              All Properties
            </Link>
            <span className="text-gray-300">|</span>
            <Link to="/houses-for-sale/johannesburg" className="text-emerald-600 hover:underline">
              Houses in Johannesburg
            </Link>
            <span className="text-gray-300">|</span>
            <Link to="/apartments-to-rent/sandton" className="text-emerald-600 hover:underline">
              Apartments in Sandton
            </Link>
            <span className="text-gray-300">|</span>
            <Link to="/properties" className="text-emerald-600 hover:underline">
              Advanced Search
            </Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default ListingDetailPage;
